import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { SiteProvider, useSite } from "@/contexts/SiteContext";

import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Weddings from "@/pages/Weddings";
import WeddingDetail from "@/pages/WeddingDetail";
import Films from "@/pages/Films";
import PreWedding from "@/pages/PreWedding";
import PreWeddingDetail from "@/pages/PreWeddingDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ClientGalleries from "@/pages/ClientGalleries";
import ClientGallery from "@/pages/ClientGallery";
import GalleryShareAccess from "@/pages/GalleryShareAccess";
import NotFound from "@/pages/NotFound";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminWeddings from "@/pages/admin/AdminWeddings";
import AdminWeddingForm from "@/pages/admin/AdminWeddingForm";
import AdminFilms from "@/pages/admin/AdminFilms";
import AdminPreWedding from "@/pages/admin/AdminPreWedding";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminInstagram from "@/pages/admin/AdminInstagram";
import AdminInquiries from "@/pages/admin/AdminInquiries";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminClientGalleries from "@/pages/admin/AdminClientGalleries";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <SeoHead />
          <Routes>
            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="weddings" element={<AdminWeddings />} />
              <Route path="weddings/new" element={<AdminWeddingForm />} />
              <Route path="weddings/:slug/edit" element={<AdminWeddingForm />} />
              <Route path="films" element={<AdminFilms />} />
              <Route path="pre-wedding" element={<AdminPreWedding />} />
              <Route path="testimonials" element={<AdminTestimonials />} />
              <Route path="instagram" element={<AdminInstagram />} />
              <Route path="inquiries" element={<AdminInquiries />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="gallery" element={<AdminGallery />} />
              <Route path="client-galleries" element={<AdminClientGalleries />} />
            </Route>

            {/* Public */}
            <Route element={<SitePublicWrapper />}>
              <Route path="/" element={<Home />} />
              <Route path="/weddings" element={<Weddings />} />
              <Route path="/weddings/:slug" element={<WeddingDetail />} />
              <Route path="/films" element={<Films />} />
              <Route path="/pre-wedding" element={<PreWedding />} />
              <Route path="/pre-wedding/:slug" element={<PreWeddingDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/client-gallery" element={<ClientGalleries />} />
              <Route path="/client-gallery/g/:shareToken" element={<ClientGalleries />} />
              <Route path="/gallery" element={<Navigate to="/client-gallery" replace />} />
              <Route path="/gallery/share/:shareToken" element={<GalleryShareAccess />} />
              <Route path="/gallery/:slug" element={<ClientGallery />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        position="bottom-center"
        toastOptions={{ style: { background: "#070707", color: "#F7F1E8", border: "1px solid #C99A45", borderRadius: 0 } }}
      />
    </div>
  );
}

const SITE_URL = "https://mangalferafilms.com";
const DEFAULT_DESCRIPTION = "Mangalfera Films creates cinematic wedding photography and films for couples across Ahmedabad, Gujarat, India, and destination weddings.";

const SEO_BY_PATH = {
  "/": {
    title: "Mangalfera Films | Wedding Photography & Films in Ahmedabad",
    description: DEFAULT_DESCRIPTION,
  },
  "/weddings": {
    title: "Wedding Photography Portfolio | Mangalfera Films",
    description: "Explore cinematic wedding photography stories captured by Mangalfera Films across Ahmedabad, Gujarat, India, and destination weddings.",
  },
  "/films": {
    title: "Cinematic Wedding Films in Ahmedabad | Mangalfera Films",
    description: "Watch cinematic wedding films created by Mangalfera Films for couples across Ahmedabad, Gujarat, India, and destination weddings.",
  },
  "/pre-wedding": {
    title: "Pre-Wedding Photography in Ahmedabad | Mangalfera Films",
    description: "Discover elegant pre-wedding photography by Mangalfera Films in Ahmedabad, Gujarat, and destination locations.",
  },
  "/about": {
    title: "About Mangalfera Films | Wedding Photographers in Ahmedabad",
    description: "Meet Urvesh Thummar and the Mangalfera Films team, creating candid photography and cinematic wedding films from Ahmedabad.",
  },
  "/contact": {
    title: "Contact Mangalfera Films | Wedding Photography Enquiry",
    description: "Contact Mangalfera Films for wedding photography, cinematic films, pre-wedding shoots, and destination wedding coverage.",
  },
};

function setMeta(selector, attribute, value) {
  const element = document.querySelector(selector);
  if (element) element.setAttribute(attribute, value);
}

function SeoHead() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const isAdmin = pathname.startsWith("/admin");
    const isPrivateGallery = pathname.startsWith("/gallery") || pathname.startsWith("/client-gallery");
    const normalizedPath = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
    const pageSeo = SEO_BY_PATH[normalizedPath] || (
      normalizedPath.startsWith("/weddings/")
        ? {
            title: "Wedding Story | Mangalfera Films",
            description: "View a cinematic wedding photography story by Mangalfera Films.",
          }
        : normalizedPath.startsWith("/pre-wedding/")
        ? {
            title: "Pre-Wedding Session | Mangalfera Films",
            description: "View a cinematic pre-wedding photography session by Mangalfera Films.",
          }
        : SEO_BY_PATH["/"]
    );
    const canonicalUrl = `${SITE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`;
    const robots = isAdmin || isPrivateGallery
      ? "noindex, nofollow"
      : "index, follow, max-image-preview:large";

    document.title = isAdmin ? "Mangalfera Films Admin" : pageSeo.title;
    setMeta('meta[name="description"]', "content", pageSeo.description);
    setMeta('meta[name="robots"]', "content", robots);
    setMeta('link[rel="canonical"]', "href", canonicalUrl);
    setMeta('meta[property="og:title"]', "content", pageSeo.title);
    setMeta('meta[property="og:description"]', "content", pageSeo.description);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[name="twitter:title"]', "content", pageSeo.title);
    setMeta('meta[name="twitter:description"]', "content", pageSeo.description);
  }, [pathname]);

  return null;
}

function SitePublicWrapper() {
  return (
    <SiteProvider>
      <PublicSiteGate />
    </SiteProvider>
  );
}

function PublicSiteGate() {
  const { siteLoading, isSiteReady } = useSite() || {};

  if (siteLoading || !isSiteReady) {
    return <SiteLoadingSplash />;
  }

  return <Layout />;
}

function SiteLoadingSplash() {
  return (
    <div
      className="min-h-screen bg-[#070707] text-[#F5EFE4] flex items-center justify-center px-6"
      data-testid="site-loading-splash"
      role="status"
      aria-label="Loading Mangalfera Films"
    >
      <div className="text-center">
        <div className="font-display text-[clamp(2rem,5vw,3.4rem)] tracking-wide">
          Mangalfera Films
        </div>
        <div className="eyebrow text-[0.56rem] text-[#B8914A] mt-3">
          Wedding Photography &amp; Films
        </div>
        <div className="w-20 h-px bg-[#B8914A]/50 mx-auto mt-7 overflow-hidden">
          <div className="w-full h-full bg-[#B8914A] animate-pulse" />
        </div>
      </div>
    </div>
  );
}

export default App;
